import * as React from "react";

import { STATUS_BADGES, StatusBadge } from "@/shared/components/StatusBadge";
import { InvocationStatus } from "@/shared/domain/actions";

const noop = () => {
  /** noop */
};

// Taken from
// https://overreacted.io/making-setinterval-declarative-with-react-hooks/
function useInterval(callback = noop, delay: number | null = 1000) {
  const savedCallback = React.useRef<() => void>(callback);

  // Remember the latest callback.
  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  React.useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

interface InvocationStatusProps {
  onPollForStatus?: () => void;
  pollInterval?: number | null; // in milliseconds
  status: InvocationStatus;
  "data-testid"?: string;
}

/** An invocation's status, polling for a newer one while it is still in flight. */
export default function InvocationStatusChip({
  onPollForStatus,
  pollInterval = 2000,
  status,
  "data-testid": testId,
}: InvocationStatusProps) {
  useInterval(
    onPollForStatus,
    status < InvocationStatus.Completed && Number.isFinite(pollInterval)
      ? pollInterval
      : null,
  );

  return (
    <StatusBadge
      status={STATUS_BADGES.invocation[status]}
      data-testid={testId}
    />
  );
}
